import { Injectable } from "@angular/core";
import {
	HttpInterceptor,
	HttpHandler,
	HttpRequest,
	HttpEvent,
	HttpErrorResponse,
	HttpClient
} from "@angular/common/http";
import { Observable, throwError, empty } from "rxjs";
import "rxjs/add/operator/do";
import "rxjs/add/operator/catch";
import { map, share, flatMap } from "rxjs/operators";

import { AuthenticationService } from "../authentication/authentication.service";
import { LoggedInUser } from "../authentication/user";
import { isImpulseError, isImpulseErrorHTTPResponse, ImpulseErrorHTTPResponse } from "../translate";

@Injectable()
export class ApiGatewayInterceptor implements HttpInterceptor {
	private AUTH_HEADER = "Authorization";

	constructor(
		private authService: AuthenticationService,
		private http: HttpClient) {
	}

	public intercept(
		request: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {

		// The authentication guard ensures that a user exists here, no need to check for undefined
		request = this.addAuthenticationToken(this.authService.getCurrentUser()!, request);

		return next.handle(request).pipe(
			map((event: HttpEvent<any>) => {
				return event;
			})).catch((err: HttpErrorResponse) => {
				return this.handleError(err, request);
			});
	}

	private handleError(err: HttpErrorResponse, req: HttpRequest<any>): Observable<any> {
		if (err.status === 401) {
			const user = this.authService.getCurrentUser();
			if (!!user && user.isExpired()) {
				const result = share<HttpEvent<unknown>>()(flatMap(() => {
					// Redo the request now that this interceptor will be able to add valid creds to the header
					return this.http.request(req);
				})(this.authService.refreshUser(user)));
				// Share is used here because the multiple subscriptions will otherwise cause the function above to be called multiple
				// times, and the API call to be made multiple times.  This is something about observables that I don't understand...
				// but share hides the multiple subscriptions from the underlying observable so it only fires once.
				return result;
			} else {
				// any other instance of a 401 other than expired token means we don't have valid credentials and should show a login page
				this.authService.logout();
			}
		} else if (
			// special case for a disabled user
			err.status === 403 &&
			isImpulseErrorHTTPResponse(err) &&
			err.error.message === "user.disabled"
		) {
			this.authService.logout({
				idle: false,
				userDisabled: true
			});
			// can't return an error message, because that will produce a toaster
			// instead we rely on the above logout state object to produce the appropriate on-page error message in the login form component
			return empty();
		}
		return throwError(err);
	}

	private addAuthenticationToken(user: LoggedInUser, request: HttpRequest<any>): HttpRequest<any> {
		if (user) {
			const token = user.getAuthToken();
			if (!token) {
				return request;
			}
			return request.clone({
				headers: request.headers.set(this.AUTH_HEADER, token)
			});

		} else {
			this.authService.logout();
			throw new Error("NotLoggedIn");
		}
	}
}
